import React, { forwardRef, type DOMAttributes, type ReactNode } from 'react';

import shouldForwardProp from '@styled-system/should-forward-prop';
import styled from '@emotion/styled';
import {
  background,
  type BackgroundProps,
  border,
  type BorderProps,
  color,
  type ColorProps,
  flexbox,
  type FlexboxProps,
  grid,
  type GridProps,
  layout,
  type LayoutProps,
  position,
  type PositionProps,
  shadow,
  type ShadowProps,
  space,
  type SpaceProps,
  compose,
  variant,
  type DisplayProps,
  display,
} from '@lucid/theme';

export type BoxTagVariantsType = 'div' | 'section' | 'main' | 'aside' | 'article' | 'header' | 'footer' | 'span';

export interface BoxProps
  extends BackgroundProps,
    BorderProps,
    ShadowProps,
    SpaceProps,
    ColorProps,
    FlexboxProps,
    GridProps,
    LayoutProps,
    PositionProps,
    DisplayProps,
    DOMAttributes<Element> {
  /** Sets the HTML tag for the Box Component. */
  as?: BoxTagVariantsType;
  /** The children to be rendered. */
  children?: ReactNode | ReactNode[];
  /** Allows the component to get additional styles. */
  className?: string;
  /** HTML id for Box Component. */
  id?: string;
  /** Sets the role for the Box Component. */
  role?: string;
  /** Sets the tabIndex for the Box Component. */
  tabIndex?: number;
}

const boxStyleProps = compose(background, shadow, border, color, grid, space, flexbox, layout, position, display);

const StyledBox = styled('div', {
  shouldForwardProp,
})(
  variant({
    variants: {
      defaultVariant: {},
    },
  }),
  boxStyleProps,
);

const Box = forwardRef<HTMLDivElement, BoxProps>(({ className, ...rest }, forwardedRef) => (
  <StyledBox ref={forwardedRef} {...rest} className={className} />
));

export { Box };
export default Box;
